import { Skeleton } from "../../elements/skeleton/skeleton";
import { textD2CSizes } from "../../elements/text-d2c/text-d2c.constants";
import { Container, type ContainerProps } from "../../layout/grid";
import { cn } from "../../lib/utils";

/** Two rows on desktop: enough to fill the first screen without a large jump. */
export const PRODUCT_LISTING_SKELETON_COUNT = 6;
const CAROUSEL_SKELETON_COUNT = 4;
const SWATCH_KEYS = ["a", "b", "c"];

function skeletonKeys(count: number, prefix: string) {
  return Array.from({ length: count }, (_, index) => `${prefix}-${index}`);
}

/** Mirrors the ProductCardD2C layout: badges row, 4:3 image, swatches, name, description, price. */
export function ProductCardD2CSkeleton() {
  return (
    <div
      className="flex h-full flex-col bg-white p-8"
      data-testid="product-card-skeleton"
    >
      <div className="min-h-[22px]" />
      <Skeleton className="aspect-[4/3] h-auto w-full" />
      <div className="flex gap-2 pt-6">
        {SWATCH_KEYS.map((key) => (
          <Skeleton className="size-7 rounded-full" key={key} />
        ))}
      </div>
      <Skeleton className="mt-4 h-6 w-3/4" />
      <Skeleton className="mt-2 h-4 w-full" />
      <Skeleton className="mt-1 h-4 w-2/3" />
      <div className="mt-auto pt-4">
        <Skeleton className="h-6 w-28" />
      </div>
    </div>
  );
}

/** Same grid as the loaded listing, so cards land where the skeletons were. */
export function ProductListingGridSkeletonItems(
  props: Readonly<{ count?: number; keyPrefix?: string }>
) {
  return skeletonKeys(
    props.count ?? PRODUCT_LISTING_SKELETON_COUNT,
    props.keyPrefix ?? "card"
  ).map((key) => (
    <div data-testid="product-listing-skeleton-item" key={key}>
      <ProductCardD2CSkeleton />
    </div>
  ));
}

export function ProductListingGridSkeleton(
  props: Readonly<{ count?: number }>
) {
  return (
    <div
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3"
      data-testid="product-listing-grid-skeleton"
    >
      <ProductListingGridSkeletonItems count={props.count} />
    </div>
  );
}

export function ProductListingCarouselSkeleton(
  props: Readonly<{ count?: number }>
) {
  return (
    <div
      className="flex overflow-hidden px-0 pl-3 md:pl-6 lg:pl-8 xl:pl-11.5"
      data-testid="product-listing-carousel-skeleton"
    >
      {skeletonKeys(props.count ?? CAROUSEL_SKELETON_COUNT, "slide").map(
        (key) => (
          <div
            className="mx-2 min-w-0 shrink-0 grow-0 basis-[85vw] first:ml-0 last:mr-0 sm:basis-[320px] md:basis-[360px] lg:basis-[380px]"
            key={key}
          >
            <ProductCardD2CSkeleton />
          </div>
        )
      )}
    </div>
  );
}

export interface ProductListingD2CSkeletonProps {
  title?: string;
  titleHeadingLevel?: "h2" | "h3";
  anchorId?: string;
  variant?: "grid" | "carousel";
  showFilters?: boolean;
  showSort?: boolean;
  paginationMode?: "paginate" | "showAll";
  count?: number;
  backgroundColor?: "black" | "white" | "whitesmoke";
  fullWidth?: ContainerProps["fullWidth"];
  paddingTop?: ContainerProps["paddingTop"];
  paddingBottom?: ContainerProps["paddingBottom"];
  /** Announced to assistive technology while the listing loads. */
  loadingLabel?: string;
}

const BACKGROUNDS = {
  black: "bg-black",
  white: "bg-white",
  whitesmoke: "bg-whitesmoke",
};

/**
 * Placeholder for a whole ProductListingD2C block while its products load:
 * the configured title, filter + sort controls, product cards and the
 * load-more pagination button.
 */
export function ProductListingD2CSkeleton(
  props: Readonly<ProductListingD2CSkeletonProps>
) {
  const fullWidth = props.fullWidth ?? true;
  const backgroundColor = props.backgroundColor ?? "white";
  const isCarousel = props.variant === "carousel";
  const showFilters = !isCarousel && (props.showFilters ?? false);
  const showSort = !isCarousel && (props.showSort ?? false);
  const showPagination =
    !isCarousel && (props.paginationMode ?? "paginate") === "paginate";
  const TitleHeading = props.titleHeadingLevel ?? "h2";

  return (
    <Container
      className={cn(
        "component-product-listing-d2c-skeleton -mb-px overflow-x-clip",
        BACKGROUNDS[backgroundColor]
      )}
      fullWidth={fullWidth}
      outerContainerOnly={fullWidth}
      paddingBottom={props.paddingBottom}
      paddingTop={props.paddingTop}
      testId="product-listing-skeleton"
    >
      <div aria-busy="true" data-skeleton="product-listing">
        {props.loadingLabel ? (
          <output className="sr-only">{props.loadingLabel}</output>
        ) : null}
        {props.title || showFilters || showSort ? (
          <div
            className="mb-6 flex flex-col justify-between gap-3 px-3 sm:flex-row sm:items-center md:px-6 lg:px-8 xl:px-11.5"
            id={props.anchorId}
          >
            {props.title ? (
              <TitleHeading
                className={cn(
                  "font-bold font-brand",
                  textD2CSizes.h3,
                  backgroundColor === "black" ? "text-white" : "text-black"
                )}
              >
                {props.title}
              </TitleHeading>
            ) : (
              <span />
            )}
            {showFilters || showSort ? (
              <div
                className={cn(
                  "grid w-full gap-2 lg:flex lg:w-auto",
                  showFilters && showSort ? "grid-cols-2" : "grid-cols-1"
                )}
                data-testid="product-listing-controls-skeleton"
              >
                {showFilters ? (
                  <Skeleton className="h-12 w-full rounded-sm lg:w-36" />
                ) : null}
                {showSort ? (
                  <Skeleton className="h-12 w-full rounded-sm lg:w-36" />
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        {isCarousel ? (
          <ProductListingCarouselSkeleton count={props.count} />
        ) : (
          <div className="px-3 md:px-6 lg:px-8 xl:px-11.5">
            <ProductListingGridSkeleton count={props.count} />
            {showPagination ? (
              <div
                className="flex justify-center pt-20"
                data-testid="product-listing-pagination-skeleton"
              >
                <Skeleton className="h-14 w-full max-w-[22.875rem] rounded-sm" />
              </div>
            ) : null}
          </div>
        )}
      </div>
    </Container>
  );
}
